// src/App.jsx
import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";

import SideNav, {
  SIDEBAR_COLLAPSED_W,
  SIDEBAR_EXPANDED_W,
} from "./components/SideNav";

import MainDashboard from "./components/dashboard/MainDashboard";
import ManageMembers from "./pages/members/ManageMemberPage";

import Profile from "./pages/profile/Profile";
import Login from "./pages/auth/Login";
import StoreSelection from "./pages/store/StoreSelection";
import ChooseAction from "./pages/choose/ChooseAction";
import ManageSubscription from "./pages/subscription/ManageSubscription";
import OrderFulfillment from "./pages/orders/OrderFulfillment";

/* ---------------- AUTH HELPERS ---------------- */
const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = !!getToken();

  /* --------- HIDE SIDENAV ROUTES -------- */
  const hideSideNav = ["/login", "/select-store", "/choose-action"].includes(
    location.pathname
  );

  /* ---------------- STATE ---------------- */
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const sidebarWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_W
    : SIDEBAR_COLLAPSED_W;

  const [selectedStore, setSelectedStore] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedStore");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (selectedStore) {
      localStorage.setItem("selectedStore", JSON.stringify(selectedStore));
    } else {
      localStorage.removeItem("selectedStore");
    }
  }, [selectedStore]);

  /* ---------------- SIDENAV NAV ---------------- */
  const handleNavigate = (key) => {
    if (key === "books") navigate("/dashboard");
    if (key === "members") navigate("/members");
    if (key === "orders") navigate("/orders");
    if (key === "profile") navigate("/profile");
  };

  /* ================= RENDER ================= */
  return (
    <div className="flex">
      {/* ---------- SIDENAV ---------- */}
      {isLoggedIn && !hideSideNav && (
        <SideNav
          selectedStore={selectedStore}
          onNavigate={handleNavigate}
          onLogout={() => {
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
            navigate("/login", { replace: true });
          }}
          onToggle={setSidebarExpanded}
        />
      )}

      {/* ---------- MAIN CONTENT ---------- */}
      <main
        style={{
          marginLeft: isLoggedIn && !hideSideNav ? sidebarWidth : 0,
          transition: "margin-left 220ms cubic-bezier(.2,.9,.2,1)",
          width: "100%",
          minHeight: "100vh",
        }}
        className="bg-white overflow-hidden"
      >
        <Routes>
          {/* ROOT */}
          <Route
            path="/"
            element={
              isLoggedIn ? (
                <Navigate to="/select-store" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* LOGIN */}
          <Route
            path="/login"
            element={
              isLoggedIn ? (
                <Navigate to="/select-store" replace />
              ) : (
                <Login />
              )
            }
          />

          {/* STORE FLOW */}
          <Route
            path="/select-store"
            element={
              isLoggedIn ? (
                <StoreSelection
                  mode="hide"
                  accessibleStoreCodes={["SQG-7F92KX34"]}
                  onSelect={(store) => {
                    setSelectedStore(store);
                    navigate("/choose-action");
                  }}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/choose-action"
            element={
              isLoggedIn ? (
                <ChooseAction selectedStore={selectedStore} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* APP */}
          <Route
            path="/dashboard"
            element={
              isLoggedIn ? (
                <MainDashboard selectedStore={selectedStore} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/members"
            element={
              isLoggedIn ? (
                <ManageMembers />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/profile"
            element={isLoggedIn ? <Profile /> : <Navigate to="/login" />}
          />
          <Route path="/manage-subscription" element={<ManageSubscription />} />
          <Route
            path="/orders"
            element={isLoggedIn ? <OrderFulfillment selectedStore={selectedStore} /> : <Navigate to="/login" />}
          />

          {/* FALLBACK */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}